import { useEffect, useMemo } from 'react';
import { useAppDispatch } from '../../utils';
import { displayedSummaryAlerts } from '../../reducers/savedBuild';
import useBapRegisterUserInteraction from '../../../utils/hooks/bap/useBapRegisterUserInteraction';
import useBapConfigChangesTracker, { matchField } from './useBapConfigChangesTracker';
const useBapGetComparedData = () => {
    var _a;
    const dispatch = useAppDispatch();
    const { userInteractedWith } = useBapRegisterUserInteraction(null);
    const { availability: { isProtectionItemUnavailable, isWarrantyUnavailable, isExteriorColorUnavailable, isInteriorColorUnavailable, isAccessoryItemUnavailable, }, mismatches: { isWarrantyMismatch, isExteriorColorMismatch, isInteriorColorMismatch, isProtectionsMismatch, isOffersMismatch, isPaymentOptionsMismatch, isAccessoriesMismatch, }, savedValues: { savedWarranty, savedWarrantyName, savedProtections, savedAccessories, savedPaymentFrequency }, currentValues: { currentVehicleAccessories, currentVehicleProtections, isPriceChangedOverall }, isReady, visibleAlerts, colorExterior, colorInterior, } = useBapConfigChangesTracker();
    const configurationChanges = useMemo(() => {
        var _a, _b, _c;
        if (!isReady) {
            return {
                isDiffPrice: false,
                isDiffOptions: false,
                hasConfigurationChanges: false,
                isReady,
            };
        }
        const isColorUnavailable = isExteriorColorUnavailable || isInteriorColorUnavailable;
        const color = isColorUnavailable
            ? {
                ...(isExteriorColorUnavailable && {
                    exterior: {
                        name: (colorExterior === null || colorExterior === void 0 ? void 0 : colorExterior.name) || '',
                        upcharge: (colorExterior === null || colorExterior === void 0 ? void 0 : colorExterior.upcharge) || 0,
                        payment: (_a = colorExterior === null || colorExterior === void 0 ? void 0 : colorExterior.frequencyPayment) !== null && _a !== void 0 ? _a : 0,
                    },
                }),
                ...(isInteriorColorUnavailable && {
                    interior: {
                        name: (colorInterior === null || colorInterior === void 0 ? void 0 : colorInterior.name) || '',
                        upcharge: (colorInterior === null || colorInterior === void 0 ? void 0 : colorInterior.upcharge) || 0,
                        payment: (_b = colorInterior === null || colorInterior === void 0 ? void 0 : colorInterior.frequencyPayment) !== null && _b !== void 0 ? _b : 0,
                    },
                }),
            }
            : undefined;
        const warranty = isWarrantyUnavailable
            ? {
                name: savedWarrantyName,
                value: savedWarranty === null || savedWarranty === void 0 ? void 0 : savedWarranty.value,
                payment: (_c = savedWarranty === null || savedWarranty === void 0 ? void 0 : savedWarranty.frequencyPayment) !== null && _c !== void 0 ? _c : 0,
            }
            : undefined;
        let ownerPrograms;
        if (isProtectionItemUnavailable) {
            ownerPrograms = [
                ...(ownerPrograms || []),
                ...(savedProtections !== null && savedProtections !== void 0 ? savedProtections : [])
                    .filter(protection => !matchField(currentVehicleProtections !== null && currentVehicleProtections !== void 0 ? currentVehicleProtections : [], protection.key, 'key'))
                    .map(protection => {
                    var _a, _b, _c, _d;
                    return {
                        key: (_a = protection.key) !== null && _a !== void 0 ? _a : '',
                        name: (_b = protection.name) !== null && _b !== void 0 ? _b : '',
                        value: (_c = protection.value) !== null && _c !== void 0 ? _c : 0,
                        payment: (_d = protection === null || protection === void 0 ? void 0 : protection.frequencyPayment) !== null && _d !== void 0 ? _d : 0,
                    };
                }),
            ];
            // prevents false positive
            if (!(ownerPrograms === null || ownerPrograms === void 0 ? void 0 : ownerPrograms.length)) {
                ownerPrograms = undefined;
            }
        }
        let accessories;
        if (isAccessoryItemUnavailable) {
            accessories = [
                ...(accessories || []),
                ...(savedAccessories !== null && savedAccessories !== void 0 ? savedAccessories : [])
                    .filter(accessory => !(currentVehicleAccessories === null || currentVehicleAccessories === void 0 ? void 0 : currentVehicleAccessories.find(({ businessKey }) => accessory.key === businessKey)))
                    .map(accessory => {
                    var _a, _b, _c, _d;
                    return {
                        key: (_a = accessory.key) !== null && _a !== void 0 ? _a : '',
                        name: (_b = accessory.name) !== null && _b !== void 0 ? _b : '',
                        value: (_c = accessory.value) !== null && _c !== void 0 ? _c : 0,
                        payment: (_d = accessory === null || accessory === void 0 ? void 0 : accessory.frequencyPayment) !== null && _d !== void 0 ? _d : 0,
                    };
                }),
            ];
            // prevents false positive
            if (!(accessories === null || accessories === void 0 ? void 0 : accessories.length)) {
                accessories = undefined;
            }
        }
        const hasConfigPageMismatches = isExteriorColorMismatch || isInteriorColorMismatch || isProtectionsMismatch || isAccessoriesMismatch;
        const hasSummaryPageMismatches = isWarrantyMismatch || isPaymentOptionsMismatch || isOffersMismatch;
        const isDiffOptions = hasConfigPageMismatches || hasSummaryPageMismatches;
        let buildHasUnavailableConfigItems = isExteriorColorUnavailable ||
            isInteriorColorUnavailable ||
            isWarrantyUnavailable ||
            isProtectionItemUnavailable ||
            isAccessoryItemUnavailable;
        const diffOptions = {
            colour: color,
            warranty,
            accessories,
            ownerPrograms,
        };
        // there's a false positive triggered when diff option values are undefined, this checks for that
        if (isDiffOptions) {
            const diffOptionsList = Object.values(diffOptions);
            const isDiffOptionsAllUndefined = !(diffOptionsList === null || diffOptionsList === void 0 ? void 0 : diffOptionsList.length) || (!!diffOptionsList.length && diffOptionsList.every(value => value === undefined));
            buildHasUnavailableConfigItems = !isDiffOptionsAllUndefined;
        }
        const buildHasUnsavedConfigChanges = hasConfigPageMismatches && (userInteractedWith === null || userInteractedWith === void 0 ? void 0 : userInteractedWith.config);
        const buildHasUnsavedSummaryChanges = hasSummaryPageMismatches && ((userInteractedWith === null || userInteractedWith === void 0 ? void 0 : userInteractedWith.warranty) || (userInteractedWith === null || userInteractedWith === void 0 ? void 0 : userInteractedWith.paymentCalculator));
        return {
            isDiffOptions: buildHasUnsavedConfigChanges || buildHasUnsavedSummaryChanges,
            isDiffPrice: isPriceChangedOverall,
            hasConfigurationChanges: buildHasUnavailableConfigItems,
            diffOptions: buildHasUnavailableConfigItems ? { ...diffOptions, savedPaymentFrequency } : undefined,
            isReady,
        };
    }, [
        isReady,
        isExteriorColorUnavailable,
        isInteriorColorUnavailable,
        isWarrantyUnavailable,
        isProtectionItemUnavailable,
        isAccessoryItemUnavailable,
        isExteriorColorMismatch,
        isInteriorColorMismatch,
        isProtectionsMismatch,
        isAccessoriesMismatch,
        isWarrantyMismatch,
        isPaymentOptionsMismatch,
        isOffersMismatch,
        isPriceChangedOverall,
        userInteractedWith,
    ]);
    useEffect(() => {
        /* This is to ensure the summary alerts are only displayed one time
        and visibility does not change based on the user's interactions */
        if (!isReady)
            return;
        if (!(visibleAlerts === null || visibleAlerts === void 0 ? void 0 : visibleAlerts.config))
            dispatch(displayedSummaryAlerts({ config: configurationChanges.hasConfigurationChanges }));
        if (!(visibleAlerts === null || visibleAlerts === void 0 ? void 0 : visibleAlerts.price))
            dispatch(displayedSummaryAlerts({ price: configurationChanges.isDiffPrice }));
    }, [isReady, configurationChanges.hasConfigurationChanges, configurationChanges.isDiffPrice]);
    // We have to hide the alerts if the user visits the config page, makes some changes and comes back to summary page
    const userHasMadeChangesToConfig = (userInteractedWith === null || userInteractedWith === void 0 ? void 0 : userInteractedWith.config) || (userInteractedWith === null || userInteractedWith === void 0 ? void 0 : userInteractedWith.warranty) || (userInteractedWith === null || userInteractedWith === void 0 ? void 0 : userInteractedWith.paymentCalculator);
    const shouldShowConfigAlert = isReady && (visibleAlerts === null || visibleAlerts === void 0 ? void 0 : visibleAlerts.config) && !userHasMadeChangesToConfig;
    const shouldShowPriceAlert = isReady && (visibleAlerts === null || visibleAlerts === void 0 ? void 0 : visibleAlerts.price) && !userHasMadeChangesToConfig;
    const isDiffOptions = (_a = configurationChanges.isDiffOptions) !== null && _a !== void 0 ? _a : false;
    return {
        ...configurationChanges,
        isDiffOptions,
        isDiffPrice: shouldShowPriceAlert,
        hasConfigurationChanges: shouldShowConfigAlert,
    };
};
export default useBapGetComparedData;
